import React from "react";
import { Hammer, Calendar } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "./ui/card";
import { CopyButton } from "./CopyButton";
import { Badge } from "./ui/badge";
import { shortAddr, timeAgo } from "../lib/utils";

export function DeployerCard({ report }) {
  const dep = report?.deployer || {};
  const hist = report?.deployer_history || {};
  const audit = report?.jupiter?.audit || {};
  const firstPool = report?.jupiter?.firstPool;
  const blockTime = dep.block_time;
  const devMints = audit.devMints;
  const recent = hist.initialize_mint_in_sample;
  return (
    <Card data-testid="deployer-card">
      <CardHeader>
        <CardTitle>
          <span className="inline-flex items-center gap-2">
            <Hammer size={16} className="text-[hsl(var(--primary))]" />
            Deployer
          </span>
        </CardTitle>
        <CardDescription>
          The wallet that paid for the first transaction touching this mint.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {dep.deployer ? (
          <div className="flex items-center justify-between gap-2 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--bg-elev-2))] px-3 py-2">
            <div className="flex flex-col">
              <span className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted))]">
                deployer wallet
              </span>
              <a
                href={`https://solscan.io/account/${dep.deployer}`}
                target="_blank"
                rel="noreferrer"
                className="font-mono text-xs hover:text-[hsl(var(--primary))]"
                title={dep.deployer}
              >
                {shortAddr(dep.deployer, 8, 8)}
              </a>
            </div>
            <CopyButton value={dep.deployer} label="Copy deployer address" />
          </div>
        ) : (
          <p className="text-xs text-[hsl(var(--muted))]">
            Deployer could not be detected from on-chain history.
          </p>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
          <Stat label="First TX" value={blockTime ? timeAgo(blockTime) : "—"} icon={<Calendar size={12} />} />
          <Stat label="Dev Mints" value={devMints != null ? String(devMints) : "—"} highlight={devMints != null && devMints >= 5 ? "danger" : devMints != null && devMints >= 2 ? "warn" : "good"} />
          <Stat label="Recent InitMint" value={recent != null ? String(recent) : "—"} highlight={recent >= 2 ? "danger" : recent === 1 ? "warn" : "good"} />
          {firstPool ? (
            <Stat
              label="First Pool"
              value={firstPool.createdAt ? new Date(firstPool.createdAt).toLocaleDateString() : "—"}
            />
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

function Stat({ label, value, highlight }) {
  let badgeClass = "";
  if (highlight === "good") badgeClass = "text-[hsl(var(--success))] border-[hsl(var(--success)/0.4)] bg-[hsl(var(--success)/0.1)]";
  if (highlight === "warn") badgeClass = "text-[hsl(var(--warning))] border-[hsl(var(--warning)/0.4)] bg-[hsl(var(--warning)/0.12)]";
  if (highlight === "danger") badgeClass = "text-[hsl(var(--danger))] border-[hsl(var(--danger)/0.4)] bg-[hsl(var(--danger)/0.12)]";
  return (
    <div className="rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--bg-elev-2))] px-3 py-2">
      <div className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted))]">
        {label}
      </div>
      <div className={`mt-1 inline-block rounded px-1.5 py-0.5 text-sm font-mono tabular-nums ${badgeClass}`}>
        {value}
      </div>
    </div>
  );
}
